import React, { useCallback, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import { useCancelTranslation } from '../hooks/useCancelTranslation';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import type { DeniedNamespace } from 'src/common/authz/isAccessDenied';
import RequiredAsterisk from 'src/components/common/util/FormHelpers';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import { INLINE_SLOT, splitAroundSlot } from 'src/i18n/inlineSlot';

interface CancelTranslationModalProps {
  visible: boolean;
  namespaceId: string;
  stringGroupId: string;
  version: number;
  onDismiss: () => void;
  onSuccess: () => void;
}

/** A failed cancellation, kept with the classification the hook made. */
interface CancelFailure {
  /** The service's own message wherever it sent one. */
  readonly message: string;
  /** True for an authorization refusal, which is rendered as one rather than as a fault. */
  readonly accessDenied: boolean;
  /**
   * The namespace a refusal named, which the alert turns into a Bindle link.
   * Absent for a fault, and for a refusal the service sent no detail with.
   */
  readonly deniedNamespace?: DeniedNamespace;
}

const CancelTranslationModal: React.FC<CancelTranslationModalProps> = ({
  visible,
  namespaceId,
  stringGroupId,
  version,
  onDismiss,
  onSuccess,
}) => {
  const { t } = useTranslation();
  // TRANSCOM will not accept a cancellation without a reason, so it is required
  // here. `reasonTouched` gates the inline error so a freshly opened modal is
  // not pre-covered in red; Confirm reveals it. The trimmed reason is sent as
  // the `reason` mutation variable and forwarded to TRANSCOM's cancelReason.
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [failure, setFailure] = useState<CancelFailure | null>(null);

  const trimmedReason = reason.trim();
  const reasonError = !trimmedReason
    ? t('sgd-cancel-reason-required')
    : undefined;

  // Routed through the hook rather than calling the mutation inline, so the
  // error classification (refusal vs fault) lives in one place instead of being
  // re-derived at every cancel call site.
  const { cancel, loading } = useCancelTranslation(
    namespaceId,
    stringGroupId,
    version,
  );

  const handleConfirm = useCallback(async () => {
    // A reason is mandatory (TRANSCOM rejects a cancellation without one), so
    // reveal the error and stop here when it is missing.
    if (reasonError) {
      setReasonTouched(true);
      return;
    }
    setFailure(null);
    const result = await cancel(trimmedReason);
    if (!result.success) {
      // The reason is deliberately NOT cleared here: a failed cancellation is
      // retryable from this same open modal, and re-typing it would be busywork.
      setFailure({
        message: result.error ?? '',
        accessDenied: result.accessDenied ?? false,
        deniedNamespace: result.deniedNamespace,
      });
      return;
    }
    setReason('');
    setReasonTouched(false);
    onSuccess();
  }, [cancel, onSuccess, reasonError, trimmedReason]);

  const handleDismiss = useCallback(() => {
    setReason('');
    setReasonTouched(false);
    setFailure(null);
    onDismiss();
  }, [onDismiss]);

  // Lead-in and body are ONE resource split on the slot, so the colon and its spacing move with
  // the language — ja/zh use no space, fr spaces before the colon.
  const [warningLeadIn, warningBody] = splitAroundSlot(
    t('sgd-cancel-warning-body', { boldEnd: INLINE_SLOT }),
  );
  const cancelWarning = (
    <>
      <strong>{warningLeadIn}</strong>
      {warningBody}
    </>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={t('sgd-cancel-translation')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={loading}>
              {t('sgd-cancel-go-back')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={loading}
              className="btn-danger"
            >
              <Box color="text-status-error" fontWeight="bold">
                {t('sgd-cancel-confirm')}
              </Box>
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">{cancelWarning}</Alert>

        {/*
          Both failures are shown MODAL-level, next to the action that produced
          them, so the user keeps their typed reason and can act on the answer.
          A refusal is routed to AccessDeniedAlert so it carries the service's
          wording alone — the generic alert would frame it as something to retry.
        */}
        {failure &&
          (failure.accessDenied ? (
            <AccessDeniedAlert
              message={failure.message}
              deniedNamespace={failure.deniedNamespace}
              data-testid="cancel-access-denied"
            />
          ) : (
            <Alert type="error">{failure.message}</Alert>
          ))}

        <FormField
          label={
            <>
              {t('sgd-cancel-reason-label')}
              <RequiredAsterisk />
            </>
          }
          errorText={reasonTouched ? reasonError : undefined}
        >
          <Textarea
            value={reason}
            onChange={({ detail }) => {
              setReason(detail.value);
              setReasonTouched(true);
            }}
            placeholder={t('sgd-cancel-reason-placeholder')}
            rows={3}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default CancelTranslationModal;
